import { Manifest, LanguageEntry, StyleEntry } from '../types/manifest';
import { GameState, GameSettings, SessionData } from '../types';
import { MIN_RANGE_GAP, SCHEMA_VERSION } from '../constants/defaults';

// --- Manifest Validation ---

const LANGUAGE_CODE_RE = /^[a-z]{2}$/;
const STYLE_KEY_RE = /^[a-z0-9_-]+$/;
const AUDIO_BASE_URL_RE = /^(https?:\/\/[\w.-]+(:[0-9]+)?(\/[\w._~:/?#@!$&'()*+,;=-]*)?|\/[\w._~:/?#@!$&'()*+,;=-]*)$/;

export function validateManifest(data: unknown): data is Manifest {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;

  if (typeof obj.version !== 'string') return false;
  if (typeof obj.lastUpdated !== 'string') return false;
  if (!obj.languages || typeof obj.languages !== 'object') return false;
  if (!obj.defaults || typeof obj.defaults !== 'object') return false;

  // Validate audioBaseUrl if present
  if (obj.audioBaseUrl !== undefined && obj.audioBaseUrl !== null && obj.audioBaseUrl !== '') {
    if (typeof obj.audioBaseUrl !== 'string') return false;
    if (!AUDIO_BASE_URL_RE.test(obj.audioBaseUrl as string)) return false;
  }

  const languages = obj.languages as Record<string, unknown>;
  const langKeys = Object.keys(languages);
  if (langKeys.length === 0) return false;

  for (const langCode of langKeys) {
    if (!LANGUAGE_CODE_RE.test(langCode)) return false;
    const lang = languages[langCode] as Record<string, unknown>;
    if (!lang || typeof lang !== 'object') return false;
    if (typeof lang.name !== 'string') return false;
    if (typeof lang.nativeName !== 'string') return false;
    if (!lang.styles || typeof lang.styles !== 'object') return false;

    const styles = lang.styles as Record<string, unknown>;
    const styleKeys = Object.keys(styles);
    if (styleKeys.length === 0) return false;

    for (const styleKey of styleKeys) {
      if (!STYLE_KEY_RE.test(styleKey)) return false;
      const style = styles[styleKey] as Record<string, unknown>;
      if (!style || typeof style !== 'object') return false;
      if (typeof style.name !== 'string') return false;
      if (typeof style.description !== 'string') return false;
      if (typeof style.files !== 'number' || !Number.isInteger(style.files) || style.files < 10) return false;
      if (style.previewUrl !== null && style.previewUrl !== undefined && typeof style.previewUrl !== 'string') return false;
    }
  }

  const defaults = obj.defaults as Record<string, unknown>;
  if (typeof defaults.language !== 'string') return false;
  if (typeof defaults.style !== 'string') return false;
  if (typeof defaults.min !== 'number') return false;
  if (typeof defaults.max !== 'number') return false;
  if (typeof defaults.autoPlayInterval !== 'number') return false;

  // Verify defaults reference valid language/style
  if (!languages[defaults.language as string]) return false;
  const defaultLang = languages[defaults.language as string] as LanguageEntry;
  if (!defaultLang.styles || !(defaultLang.styles as Record<string, unknown>)[defaults.style as string]) return false;

  return true;
}

// --- Range Validation ---

export function validateRange(min: number, max: number, maxFiles?: number): { valid: boolean; error?: string } {
  if (!Number.isInteger(min) || !Number.isInteger(max)) {
    return { valid: false, error: 'Min and max must be integers' };
  }
  if (min < 1) {
    return { valid: false, error: 'Minimum must be at least 1' };
  }
  if (min >= max) {
    return { valid: false, error: 'Minimum must be less than Maximum' };
  }
  if (max - min + 1 < MIN_RANGE_GAP) {
    return { valid: false, error: `Range must contain at least ${MIN_RANGE_GAP} numbers` };
  }
  if (maxFiles !== undefined && max > maxFiles) {
    return { valid: false, error: `Maximum (${max}) exceeds available audio files (${maxFiles}) for this style` };
  }
  return { valid: true };
}

// --- State Validation ---

export function validateStateStructure(state: unknown): state is GameState {
  if (!state || typeof state !== 'object') return false;
  const obj = state as Record<string, unknown>;

  if (typeof obj.version !== 'number') return false;
  if (typeof obj.timestamp !== 'string') return false;
  if (!['IDLE', 'PLAYING', 'COMPLETE'].includes(obj.gamePhase as string)) return false;

  if (!obj.settings || typeof obj.settings !== 'object') return false;
  const settings = obj.settings as Record<string, unknown>;
  if (typeof settings.language !== 'string') return false;
  if (typeof settings.style !== 'string') return false;
  if (typeof settings.min !== 'number') return false;
  if (typeof settings.max !== 'number') return false;
  if (typeof settings.autoPlayEnabled !== 'boolean') return false;
  if (typeof settings.autoPlayInterval !== 'number') return false;

  if (!obj.session || typeof obj.session !== 'object') return false;
  const session = obj.session as Record<string, unknown>;
  if (!Array.isArray(session.calledNumbers)) return false;
  if (!Array.isArray(session.shuffledQueue)) return false;

  // Validate every number in calledNumbers is a number
  for (const n of session.calledNumbers as unknown[]) {
    if (typeof n !== 'number' || !Number.isInteger(n)) return false;
  }
  for (const n of session.shuffledQueue as unknown[]) {
    if (typeof n !== 'number' || !Number.isInteger(n)) return false;
  }

  return true;
}

export function validateStateAgainstManifest(state: GameState, manifest: Manifest): boolean {
  const { language, style, min, max } = state.settings;

  // Check language exists
  if (!manifest.languages[language]) return false;

  // Check style exists for language
  const langEntry = manifest.languages[language];
  if (!langEntry.styles[style]) return false;

  // Check range is valid for style
  const styleEntry = langEntry.styles[style];
  if (min < 1 || max > styleEntry.files) return false;
  if (max - min + 1 < MIN_RANGE_GAP) return false;

  // Check all calledNumbers and shuffledQueue within range
  const allNumbers = [...state.session.calledNumbers, ...state.session.shuffledQueue];
  for (const n of allNumbers) {
    if (n < min || n > max) return false;
  }

  return true;
}
